import {
  ActionSettingTypeCodes,
  StepAction,
  TriggerDropdownOptions,
} from '../models';
import { ActionSelected } from '../models';
import { DeeplinkParamValues } from '../models/PanelActionsSettings';
import { serializeDateTimeFormat } from './formActionsSettingsTypes';
import { PrePopulatedTypeEnum } from '../api/model/pre-populated-type-enum';
import { PrePopulatedFormType } from './formActionsSettingsTypes';

const LABEL_LINE_BREAK_TAGS =
  'p,div,li,ul,ol,h1,h2,h3,h4,h5,h6,blockquote,pre,table,tr,td,th,hr,br';

export const hasAdjacentLabelTokens = (html: string): boolean => {
  if (!html) return false;
  // DOMParser yields an inert document: no script execution, no <img> fetches.
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  // Tokens on different visual lines are not adjacent — mark every line-breaking
  // boundary so only a same-line `}}{{` survives the textContent flatten.
  body.querySelectorAll(LABEL_LINE_BREAK_TAGS).forEach(node => {
    node.insertAdjacentText('beforebegin', '\n');
    node.insertAdjacentText('afterend', '\n');
  });
  return /\}\}\{\{/.test(body.textContent ?? '');
};

// Saved token elements (<action>, <json>, <registration>) are empty in the DB.
// Two consecutive token elements with no characters between them have no delimiter.
// A space, comma, or any other character counts as a valid separator — mirrors the
// plain-text check (}}{{}}) which also only fires on zero-character adjacency.
export const hasAdjacentActionTags = (html: string): boolean => {
  if (!html) return false;
  return /<\/(action|json|registration)><(action|json|registration)/i.test(
    html
  );
};

const handleSaveUpdateActionProperties = (
  formActions: Record<string, Record<string, string>>,
  actionSettings: ActionSettingTypeCodes,
  stepActionSelected: ActionSelected | undefined,
  handleSaveUpdateActionPropertiesMutation: (
    updatedFormActions: Record<string, string>
  ) => void
) => {
  const formActionSettings = formActions[actionSettings];
  const updateProperties =
    typeof formActionSettings === 'object' && formActionSettings !== null
      ? { ...formActionSettings }
      : {};
  const updatedFormActions = Object.keys(updateProperties).reduce(
    (result, key) => {
      const properties = stepActionSelected?.properties;
      const oldValue = properties?.[key];
      const valueToParse = updateProperties[key];
      let newValue = '';

      if (valueToParse !== null && typeof valueToParse === 'object') {
        const value = valueToParse?.['value'];
        const isChoicesKeyAndArray =
          key === 'choices' && Array.isArray(valueToParse);
        const valuetoStringify =
          isChoicesKeyAndArray &&
          (valueToParse as string[]).filter((v: string) => v.length);
        newValue =
          value ??
          (valuetoStringify && valuetoStringify.length
            ? JSON.stringify(valuetoStringify)
            : valueToParse);
      } else {
        newValue = valueToParse;
      }
      if (
        newValue !== null &&
        typeof newValue !== 'object' &&
        (properties == null || !(key in properties)
          ? newValue !== ''
          : String(newValue) !== oldValue)
      ) {
        result[key] = String(newValue);
      }
      return result;
    },
    {} as typeof updateProperties
  );
  // Send every changed property in one request. Previously this fired one PUT per property,
  // so a six-field edit opened six concurrent transactions that each re-resolved the version,
  // re-ran the commit-option guard and re-touched the audit rows, and a failure part-way
  // through left some fields saved and the rest dropped. The batch endpoint saves them in a
  // single transaction, so that fixed cost is paid once and the edit is all-or-nothing.
  if (Object.keys(updatedFormActions).length > 0)
    handleSaveUpdateActionPropertiesMutation(updatedFormActions);
};

// TODO: Make a refactor of this function to make it more readable
export const handleFormValueChanges = (
  formActions: Record<string, Record<string, string>>,
  actionSettings: ActionSettingTypeCodes,
  stepActionSelected: ActionSelected | undefined,
  handleSaveUpdateActionPropertiesMutation: (
    updatedFormActions: Record<string, string>
  ) => void,
  handleSaveUpdateAction: (stepAction?: StepAction) => void
) => {
  formActions[actionSettings] = handleActionSpecificLogic(
    formActions,
    actionSettings
  );

  handleSaveUpdateActionProperties(
    formActions,
    actionSettings,
    stepActionSelected,
    handleSaveUpdateActionPropertiesMutation
  );

  let prepopulate: Record<string, Record<string, Record<string, string>>>;
  const jsonRight: number[] = [],
    jsonRightSelected: number[] = [];
  if (actionSettings === ActionSettingTypeCodes.DATETIME) {
    prepopulate = formActions[actionSettings]?.[
      `PREPOPULATE_${formActions[actionSettings]['type']}`
    ] as never as Record<string, Record<string, Record<string, string>>>;
    let index = 1;
    Object.keys(prepopulate).forEach(key => {
      if (
        key === `json${index}` &&
        prepopulate[key]?.[`json${index}Right`]?.['jsonFieldId']
      ) {
        jsonRight.push(
          Number(prepopulate[key][`json${index}Right`]['jsonFieldId'])
        );
        index++;
      }
    });
  } else if (
    actionSettings === ActionSettingTypeCodes.DROPDOWN_SELECT ||
    actionSettings === ActionSettingTypeCodes.RADIOCHECKBOX
  ) {
    prepopulate = formActions[actionSettings]?.[
      'prepopulate'
    ] as never as Record<string, Record<string, Record<string, string>>>;
    let i = 0;
    while (prepopulate?.[`choice${i}`] !== undefined) {
      const right = prepopulate[`choice${i}`]?.[`choice${i}Right`];
      if (right?.['jsonFieldId']) {
        jsonRight.push(Number(right['jsonFieldId']));
      }
      i++;
    }
  } else {
    prepopulate = formActions[actionSettings]?.[
      'prepopulate'
    ] as never as Record<string, Record<string, Record<string, string>>>;
    if (prepopulate?.['json']?.['jsonRight']?.['jsonFieldId']) {
      jsonRight.push(Number(prepopulate['json']['jsonRight']['jsonFieldId']));
    }
  }

  stepActionSelected?.prePopulatedValues?.forEach(
    o => o.jsonFieldId && jsonRightSelected.push(o.jsonFieldId)
  );

  // DROPDOWN_SELECT and RADIOCHECKBOX use per-choice prepopulate keys (choice0,
  // choice1, …) instead of the single 'json' key that all other action types use.
  // The shared typeChanged / regFieldChanged / extApiChanged / workflowComponentChanged
  // checks below read prepopulateJson['json'] which is undefined for these two types,
  // making typeChanged spuriously true whenever the server has a non-JSON source type —
  // that falsely triggers handleSaveUpdateAction() on every debounce, which fires
  // initForm(), kills the formValueSubscription, and rebuilds the form from stale server
  // data before the choices batch-properties mutation can complete.
  // Instead, detect per-choice source changes here and skip the shared checks below.
  const isPerChoicePrepopulate =
    actionSettings === ActionSettingTypeCodes.DROPDOWN_SELECT ||
    actionSettings === ActionSettingTypeCodes.RADIOCHECKBOX;

  // Only fire the action-level save when action-level fields actually changed.
  // Property-level fields (minLen, maxLen, label, etc.) are already saved by
  // handleSaveUpdateActionProperties above. Firing the action save on every
  // debounce causes a race condition where the backend's UpdateAction overwrites
  // concurrently saved property values.
  const currentIsReadOnly = !!(
    prepopulate as unknown as Record<string, unknown>
  )?.['isReadOnly'];

  const actionLevelChanged =
    !!formActions['isRequired'] !== !!stepActionSelected?.isRequired ||
    !!formActions['isActionConditional'] !==
      !!stepActionSelected?.isActionConditional ||
    !!formActions['isVisible'] !== !!stepActionSelected?.isVisible ||
    !!formActions['isRepeatable'] !== !!stepActionSelected?.isRepeatable ||
    currentIsReadOnly !== !!stepActionSelected?.isReadOnly;

  const currentIsPrePopulated = !!(
    prepopulate as unknown as Record<string, unknown>
  )?.['isPrePopulated'];
  const prevIsPrePopulated = !!stepActionSelected?.isPrePopulated;

  const prepopulateJson = prepopulate as unknown as Record<
    string,
    Record<string, Record<string, string>>
  >;
  const currentLeftType =
    prepopulateJson?.['json']?.['jsonLeft']?.['value'] ?? '';
  const serverPrePopVal = stepActionSelected?.prePopulatedValues?.[0];

  const typeMap: Record<string, PrePopulatedTypeEnum> = {
    [PrePopulatedFormType.RegField]: PrePopulatedTypeEnum.RegField,
    [PrePopulatedFormType.ExternApi]: PrePopulatedTypeEnum.ExternApi,
    [PrePopulatedFormType.WfComp]: PrePopulatedTypeEnum.WfComp,
    [PrePopulatedFormType.Json]: PrePopulatedTypeEnum.Json,
    '': PrePopulatedTypeEnum.Json,
  };

  // Per-choice change detection for DROPDOWN_SELECT / RADIOCHECKBOX.
  // Checks whether any choice's source type or value differs from the server.
  let perChoicePrePopulateChanged = false;
  if (isPerChoicePrepopulate && currentIsPrePopulated && prevIsPrePopulated) {
    const serverValues = stepActionSelected?.prePopulatedValues ?? [];
    let j = 0;
    while (prepopulate?.[`choice${j}`] !== undefined) {
      const entry = prepopulate[`choice${j}`] as Record<
        string,
        Record<string, unknown>
      >;
      const leftType =
        (entry?.[`choice${j}Left`] as Record<string, string>)?.['value'] ?? '';
      const right = entry?.[`choice${j}Right`] as Record<string, unknown>;
      const sv = serverValues[j];
      const currentType = typeMap[leftType] ?? PrePopulatedTypeEnum.Json;
      const serverType = sv?.type ?? PrePopulatedTypeEnum.Json;
      if (currentType !== serverType) {
        perChoicePrePopulateChanged = true;
        break;
      }
      if (
        leftType === PrePopulatedFormType.RegField &&
        Number(right?.['regFieldId'] ?? 0) !== (sv?.regFieldId ?? 0)
      ) {
        perChoicePrePopulateChanged = true;
        break;
      }
      if (
        leftType === PrePopulatedFormType.ExternApi &&
        String(right ?? '') !== String(sv?.value ?? '')
      ) {
        perChoicePrePopulateChanged = true;
        break;
      }
      if (
        leftType === PrePopulatedFormType.WfComp &&
        (Number(right?.['data']) || 0) !== (sv?.sourceActionRefId ?? 0)
      ) {
        perChoicePrePopulateChanged = true;
        break;
      }
      j++;
    }
  }

  // Guard: typeChanged / regFieldChanged / extApiChanged / workflowComponentChanged
  // use prepopulateJson['json'] which is undefined for per-choice action types.
  // For those types, perChoicePrePopulateChanged (above) handles all source changes.
  const typeChanged =
    !isPerChoicePrepopulate &&
    currentIsPrePopulated &&
    prevIsPrePopulated &&
    typeMap[currentLeftType] !== serverPrePopVal?.type;

  const regFieldChanged =
    currentLeftType === PrePopulatedFormType.RegField &&
    Number(prepopulateJson?.['json']?.['jsonRight']?.['regFieldId']) !==
      serverPrePopVal?.regFieldId;

  const extApiChanged =
    currentLeftType === PrePopulatedFormType.ExternApi &&
    String(prepopulateJson?.['json']?.['jsonRight'] ?? '') !==
      String(serverPrePopVal?.value ?? '');

  const workflowComponentChanged =
    currentLeftType === PrePopulatedFormType.WfComp &&
    (Number(prepopulateJson?.['json']?.['jsonRight']?.['data']) || 0) !==
      (serverPrePopVal?.sourceActionRefId ?? 0);

  const prePopulateChanged =
    currentIsPrePopulated !== prevIsPrePopulated ||
    JSON.stringify(jsonRight) !== JSON.stringify(jsonRightSelected) ||
    (isPerChoicePrepopulate
      ? perChoicePrePopulateChanged
      : typeChanged ||
        regFieldChanged ||
        extApiChanged ||
        workflowComponentChanged);

  // For TFLO_EMAIL/TFLO_SMS the pre-populate toggle is stored as the
  // prePopulateRecipient property, not at the action level. handleSaveUpdateAction
  // would send isPrePopulated + prePopulatedValues which the server rejects.
  // handleSaveUpdateActionProperties already saves prePopulateRecipient.
  const isTFLO =
    actionSettings === ActionSettingTypeCodes.TFLO_EMAIL ||
    actionSettings === ActionSettingTypeCodes.TFLO_SMS;

  if (actionLevelChanged || (!isTFLO && prePopulateChanged)) {
    handleSaveUpdateAction();
  }
};

function serializeButtonValue(
  btnVal: Record<string, unknown>
): Record<string, unknown> {
  const recipientPrepopulate = btnVal?.[
    'recipientPrepopulate'
  ] as unknown as Record<string, Record<string, Record<string, string>>>;
  const actionIds = btnVal?.['actionIds'] as unknown as Record<string, string>;
  const scanDivisionIdRaw = btnVal?.['scanDivisionId'] as unknown as
    | Record<string, string>
    | string;
  const scanParamsRaw = btnVal?.['scanParams'] as unknown as Record<
    string,
    Record<string, string> | string
  >[];
  const serializedScanParams = Array.isArray(scanParamsRaw)
    ? JSON.stringify(
        scanParamsRaw.map(p => {
          const source =
            (p['source'] as Record<string, string>)?.['value'] ??
            (p['source'] as string) ??
            '';
          const rawVal = p['value'];
          if (source === 'DEEPLINK') {
            const selected = Array.isArray(rawVal)
              ? (rawVal as Record<string, string>[])
              : [];
            const inputs =
              (p['inputs'] as Record<string, string> | undefined) ?? {};
            const orderIndex = new Map(
              DeeplinkParamValues.map((o, i) => [o.value, i])
            );
            const orderedKeys = selected
              .map(opt => opt?.['value'] ?? String(opt ?? ''))
              .filter(k => !!k)
              .sort(
                (a, b) =>
                  (orderIndex.get(a) ?? Number.MAX_SAFE_INTEGER) -
                  (orderIndex.get(b) ?? Number.MAX_SAFE_INTEGER)
              );
            const value = orderedKeys.reduce<Record<string, string>>(
              (acc, key) => {
                acc[key] = inputs[key] ?? '';
                return acc;
              },
              {}
            );
            return { source, value };
          }
          const value =
            source === 'WORKFLOW_COMPONENT'
              ? (rawVal as Record<string, unknown>)?.['actionId'] != null
                ? String((rawVal as Record<string, unknown>)['actionId'])
                : ((rawVal as Record<string, string>)?.['data'] ??
                  (rawVal as string | null | undefined) ??
                  '')
              : Array.isArray(rawVal)
                ? (rawVal as Record<string, string>[]).map(
                    v => v?.['value'] ?? String(v ?? '')
                  )
                : ((rawVal as Record<string, string>)?.['value'] ??
                  (rawVal as Record<string, string>)?.['data'] ??
                  (rawVal as string | null | undefined) ??
                  '');
          return { source, value };
        })
      )
    : '';
  const isPrePopulated = !!recipientPrepopulate?.['isPrePopulated'];
  return {
    ...btnVal,
    prePopulateRecipient: `${isPrePopulated}`,
    prePopulateValue: `${recipientPrepopulate?.['json']?.['jsonRight']?.['jsonFieldId'] ?? ''}`,
    actionIds: actionIds?.['data'] ? JSON.stringify([actionIds?.['data']]) : '',
    scanDivisionId:
      (scanDivisionIdRaw as Record<string, string>)?.['value'] ??
      (scanDivisionIdRaw as string) ??
      '',
    scanParams: serializedScanParams,
  };
}

function handleActionSpecificLogic(
  formActions: Record<string, Record<string, string>>,
  actionSettings: ActionSettingTypeCodes
): Record<string, string> {
  if (actionSettings === ActionSettingTypeCodes.DATETIME) {
    const fa = formActions[actionSettings] as unknown as Record<string, string>;
    const type = (fa['type'] as string) ?? '';
    const dateFormat = (fa['dateFormat'] as string) ?? '';
    const timeFormat = (fa['timeFormat'] as string) ?? '';
    // Strip the per-input form fields so only the combined `format` property
    // gets sent to the backend via the generic per-property PUT.
    const stripped = { ...formActions[actionSettings] };
    delete stripped['dateFormat'];
    delete stripped['timeFormat'];
    delete stripped['combinedFormat'];
    return {
      ...stripped,
      format: serializeDateTimeFormat(type, dateFormat, timeFormat),
    };
  }

  if (
    actionSettings === ActionSettingTypeCodes.DEVICE_LOCATION ||
    actionSettings === ActionSettingTypeCodes.ELD_LOCATION ||
    actionSettings === ActionSettingTypeCodes.PUSH
  ) {
    const trigger = formActions[actionSettings]?.[
      'trigger'
    ] as unknown as Record<string, Record<string, string>>;
    return {
      ...formActions[actionSettings],
      triggerType: trigger['triggerType']?.['value'] ?? '',
      triggerId: trigger['triggerId']?.['data'] ?? '',
    };
  }

  if (actionSettings === ActionSettingTypeCodes.GEOFENCE) {
    const latJsonFieldId = formActions[actionSettings]?.[
      'latJsonFieldId'
    ] as unknown as Record<string, string>;
    const longJsonFieldId = formActions[actionSettings]?.[
      'longJsonFieldId'
    ] as unknown as Record<string, string>;
    const zoneJsonFieldIdRaw = formActions[actionSettings]?.['zoneJsonFieldId'];
    const zoneJsonFieldId =
      zoneJsonFieldIdRaw && typeof zoneJsonFieldIdRaw === 'object'
        ? ((zoneJsonFieldIdRaw as Record<string, string>)['jsonFieldId'] ?? '')
        : ((zoneJsonFieldIdRaw as string) ?? '');
    return {
      ...formActions[actionSettings],
      latJsonFieldId: latJsonFieldId['jsonFieldId'] ?? '',
      longJsonFieldId: longJsonFieldId['jsonFieldId'] ?? '',
      zoneJsonFieldId,
    };
  }

  if (actionSettings === ActionSettingTypeCodes.SIDE_BY_SIDE_BUTTONS) {
    const formActionSettings = formActions[actionSettings];
    let newProperties: Record<string, string> = {};
    Object.entries(formActionSettings).forEach(([, value], index) => {
      const serialized = serializeButtonValue(
        value as unknown as Record<string, unknown>
      );
      newProperties = {
        ...newProperties,
        ...Object.entries(serialized).reduce(
          (result, [keyP, valP]) => {
            const mappedKey = keyP === 'phoneNumber' ? 'number' : keyP;
            result[`${mappedKey}${index + 1}`] = valP as string;
            return result;
          },
          {} as Record<string, string>
        ),
      };
    });
    return newProperties;
  }

  if (actionSettings === ActionSettingTypeCodes.SIMPLE_BUTTON) {
    return serializeButtonValue(
      formActions[actionSettings] as unknown as Record<string, unknown>
    ) as Record<string, string>;
  }

  if (actionSettings === ActionSettingTypeCodes.STOP) {
    const trigger = formActions[actionSettings]?.[
      'trigger'
    ] as unknown as Record<string, Record<string, string>>;
    const event = formActions[actionSettings]?.['event'] as unknown as Record<
      string,
      string
    >;
    return {
      ...formActions[actionSettings],
      triggerType: trigger['triggerType']?.['value'] ?? '',
      triggerId: trigger['triggerId']?.['data'] ?? '',
      event: event?.['value'] ?? '',
    };
  }

  if (actionSettings === ActionSettingTypeCodes.TIMER_STOPWATCH) {
    const startTrigger = formActions[actionSettings]?.[
      'startTrigger'
    ] as unknown as Record<string, Record<string, string>>;
    const stopTrigger = formActions[actionSettings]?.[
      'stopTrigger'
    ] as unknown as Record<string, Record<string, string>>;
    return {
      ...formActions[actionSettings],
      startTriggerType: startTrigger['triggerType']?.['value'] ?? '',
      startTriggerId: startTrigger['triggerId']?.['data'] ?? '',
      stopTriggerType: stopTrigger['triggerType']?.['value'] ?? '',
      stopTriggerId: stopTrigger['triggerId']?.['data'] ?? '',
    };
  }

  if (
    actionSettings === ActionSettingTypeCodes.TFLO_EMAIL ||
    actionSettings === ActionSettingTypeCodes.TFLO_SMS
  ) {
    const trigger = formActions[actionSettings]?.[
      'trigger'
    ] as unknown as Record<string, Record<string, string>>;
    const formActionSettings = formActions[actionSettings] as unknown as Record<
      string,
      Record<string, Record<string, string>>
    >;
    const actionIds = formActions[actionSettings]?.[
      'actionIds'
    ] as unknown as Record<string, string>;
    const prepopulate = formActions[actionSettings]?.[
      'prepopulate'
    ] as unknown as Record<string, unknown>;
    const isPrePopulated = !!prepopulate?.['isPrePopulated'];
    const jsonFieldId =
      formActionSettings['json']?.['jsonRight']?.['jsonFieldId'] ?? null;

    if (isPrePopulated) {
      // With the toggle ON the recipient field the user picks lives in the
      // pre-populate panel; the outer `json` control is hidden and blanked.
      const prePopulateJsonFieldId =
        (prepopulate?.['json'] as Record<string, Record<string, string>>)?.[
          'jsonRight'
        ]?.['jsonFieldId'] ?? null;

      // The backend validates triggerType as an enum and rejects an empty
      // string, so reset it to the dropdown default rather than clearing it —
      // that is also what the form renders when no trigger is stored.
      return {
        ...formActions[actionSettings],
        prePopulateRecipient: 'true',
        prePopulateValue: `${prePopulateJsonFieldId ?? ''}`,
        triggerType: TriggerDropdownOptions[0].value,
        triggerId: '',
        actionIds: '',
      };
    }

    return {
      ...formActions[actionSettings],
      triggerType: trigger['triggerType']?.['value'] ?? '',
      triggerId: trigger['triggerId']?.['data'] ?? '',
      prePopulateValue: `${jsonFieldId ?? ''}`,
      prePopulateRecipient: 'false',
      actionIds: actionIds?.['data']
        ? JSON.stringify([actionIds?.['data']])
        : '',
    };
  }

  return formActions[actionSettings];
}
